import { provide } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {
  getShowOrdersForDebtor,
  OrderDto2,
  OrderListDto,
  OrderStatus,
} from '@/api-sdk';
import {
  iconCheckmarkCircle,
  iconChevronLink,
  iconCircleInfo,
  iconExclamationCircle,
  iconGear,
} from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import { BREAKPOINT_EXTRA_LARGE_PX } from '@/settings';
import { baseStyles, rootVariablesStyles, typographyStyles } from '@/styles';
import { LayoutType } from '@/types';
import { formatDate, i18nInit, setBaseUrl } from '@/utils';

import '../loader/fg-loader';
import '../pagination/fg-pagination';
import './fg-order-history-filters';

@customElement('fg-order-history-list')
export class FgOrderHistoryList extends LitElement {
  @property({ type: String })
  debtor?: string;

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  locale = 'nl_NL';

  @property({ type: String })
  orderDetailUrl = '';

  @property({ type: String })
  status?: OrderStatus | undefined;

  @property({ type: String })
  layout: LayoutType = 'wholesale';

  @provide({ context: global.context })
  globalContext: GlobalComponentType = {
    layout: this.layout,
    locale: this.locale,
    baseUrl: this.baseUrl,
    debtor: this.debtor,
  };

  @state()
  orderDataPromise?: Promise<OrderListDto>;

  @state()
  orderData?: OrderListDto;

  @state()
  pages: number = 1;

  @state()
  currentPage = 1;

  @state()
  loading = true;

  @state()
  filtered = false;

  @state()
  searchFilters?: { searchString?: string; status?: OrderStatus };

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    this.globalContext = {
      layout: this.layout,
      locale: this.locale,
      baseUrl: this.baseUrl,
      debtor: this.debtor,
    };

    window.i18next = window.i18next || i18nInit(this.globalContext.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  protected async firstUpdated() {
    await this.getOrderList();
    this.dispatchEvent(
      new CustomEvent('order-history-count', {
        composed: true,
        detail: {
          ordersCount: this.orderData?.orders?.length ?? 0,
        },
      }),
    );
  }

  async getOrderList(
    page?: number,
    filters?: { searchString?: string; status?: OrderStatus },
  ) {
    if (!this.globalContext.debtor) {
      this.loading = false;
      return;
    }

    if (page) {
      this.currentPage = page;
    }

    this.loading = true;

    if (filters) {
      this.filtered = true;
    }

    this.searchFilters = {
      searchString: filters?.searchString,
      status: this.status ?? filters?.status,
    };

    try {
      this.orderDataPromise = getShowOrdersForDebtor({
        debtorCode: this.globalContext.debtor,
        params: {
          ...this.searchFilters,
          page: this.currentPage,
          pageSize: 10,
        },
      });

      this.orderData = await this.orderDataPromise;

      this.pages = this.orderData?.pages || 1;
      this.loading = true;
    } catch (error) {
      console.error(
        'Something went wrong while fetching the order list',
        error,
      );
    } finally {
      this.loading = false;
    }
  }

  renderOrderStatus(status: OrderStatus): TemplateResult {
    const statusTemplates: {
      [key in OrderStatus]: TemplateResult;
    } = {
      pending: html`
        <strong class="pending">
          ${iconGear} ${t('content.printProof.statusOrderPending')}
        </strong>
      `,
      print_proof_review: html`
        <strong class="review">
          <span>${iconExclamationCircle}</span>
          ${t('content.printProof.statusOrderReview')}
        </strong>
      `,
      fulfilled: html`
        <strong class="complete">
          ${iconCheckmarkCircle} ${t('content.printProof.statusOrderFulfilled')}
        </strong>
      `,
    };

    return statusTemplates[status];
  }

  createOrderDetailLink(salesOrderNumber: string): string {
    if (this.orderDetailUrl) {
      return `${this.orderDetailUrl}/${salesOrderNumber}`;
    }
    return '';
  }

  render() {
    if (this.loading && !this.orderData) {
      return html`
        <fg-loader></fg-loader>
      `;
    }

    if (
      ((!this.orderData && !this.loading) ||
        (this.orderData?.orders.length === 0 && !this.filtered)) &&
      this.globalContext.debtor
    ) {
      return html`
        <div class="order-note">
          <span>${t('content.noOrders')}</span>
        </div>
      `;
    }
    return html`
      ${this.status
        ? nothing
        : html`
            <fg-order-history-filters
              part=${this.globalContext.layout}
              @on-filter=${(event: CustomEvent) =>
                this.getOrderList(1, event.detail)}
            ></fg-order-history-filters>
          `}
      <div class="table-head">
        <strong>${t('labels.order')}</strong>
        <strong>${t('labels.status')}</strong>
        <strong>${t('labels.total')}</strong>
        <strong>${t('labels.reference')}</strong>
        <strong>${t('labels.orderedBy')}</strong>
        <strong></strong>
      </div>
      <div class="order-note">
        ${this.orderData?.orders.length === 0 &&
        this.filtered &&
        this.globalContext.debtor
          ? html`
              <span>${iconCircleInfo}</span>
              ${t('content.noOrdersFound')}
            `
          : html`
              <span>${iconCircleInfo}</span>
              ${t('content.orderHistoryNote')}
            `}
      </div>
      <div class="order-items">
        ${this.loading
          ? html`
              <fg-loader
                class=${classMap({
                  top: this.orderData?.orders.length === 0,
                })}
                .type=${'in-component'}
              ></fg-loader>
            `
          : nothing}
        ${this.orderData?.orders?.map((order: OrderDto2) => {
          const {
            orderDate,
            orderReference,
            salesOrderNumber,
            totalPrice,
            status,
            creator,
          } = order;

          return html`
          <a href=${this.createOrderDetailLink(salesOrderNumber)} title=${`Order detail #${salesOrderNumber}`}>
            <div class="order-item">
              <div class="order-item-info">
                  <strong><span>${t('labels.order')}:</span> #${salesOrderNumber}</strong>
                  ${
                    orderDate &&
                    html`
                      <span>
                        ${formatDate(orderDate, this.locale, 'short')}
                      </span>
                    `
                  }
              </div>
              <div class="order-item-status">${this.renderOrderStatus(status as OrderStatus)}</div>
              <div class="order-item-total"><span>${totalPrice.formatted}</span></div>
              <div class="order-item-reference" title=${orderReference || ''}>
                  ${
                    orderReference &&
                    html`
                      <strong>${t('labels.reference')}:</strong>
                      <span>${orderReference}</span>
                    `
                  }
              </div>
              <div class="order-item-customer">
                ${
                  creator &&
                  html`
                    <strong>${t('labels.orderedBy')}:</strong>
                    <span>${creator}</span>
                  `
                }
              </div>
              <div class="order-item-icon"><span>${iconChevronLink}</span></div>
              </div>
            </div>
          </a>`;
        })}
      </div>
      ${this.pages > 1
        ? html`
            <fg-pagination
              part="wholesale"
              .pages=${this.pages}
              .currentPage=${this.currentPage}
              @go-to-page=${(event: CustomEvent<{ page: number }>) => {
                const { page } = event.detail;

                if (page) {
                  this.getOrderList(page, this.searchFilters);
                  this.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            ></fg-pagination>
          `
        : nothing}
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    rootVariablesStyles,
    css`
      .table-head {
        display: none;
        grid-template-columns: repeat(5, minmax(100px, 1fr)) 24px;
        gap: 16px;
        padding: 0 16px;
      }

      .order-note {
        padding: 12px 16px;
        background-color: var(
          --fg-infobox-background,
          var(--fg-fallback-infobox-background)
        );
        color: var(--fg-infobox-content, var(--fg-fallback-infobox-content));
        border: 1px solid
          var(--fg-infobox-content, var(--fg-fallback-infobox-content));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        display: flex;
        gap: 4px;
        margin: 8px 0;
      }

      .order-note svg {
        width: 16px;
        height: 16px;
        fill: var(--fg-infobox-content, var(--fg-fallback-infobox-content));
      }

      a {
        display: block;
        text-decoration: none;
      }

      a:not(:last-of-type) {
        margin-bottom: 8px;
      }

      .order-items {
        position: relative;
      }

      .order-item {
        display: grid;
        grid-template-columns: minmax(100px, 90%) minmax(24px, 10%);
        color: var(--fg-text, var(--fg-fallback-text));
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        padding: 16px;
        gap: 8px;
        align-items: center;
      }

      .order-item-status strong {
        display: flex;
        gap: 4px;
      }

      .order-item-status span {
        width: 16px;
        height: 16px;
      }

      .order-item-status strong.complete,
      .order-item-status strong.pending {
        align-items: center;
      }

      .order-item-status strong.complete {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
      }

      .order-item-status strong.review {
        color: var(--fg-infobox-content, var(--fg-fallback-infobox-content));
      }

      .order-item-total {
        display: none;
      }

      .order-item-icon svg {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        width: 9px;
        height: auto;
      }

      fg-pagination {
        margin-top: 24px;
      }

      .order-item:hover {
        background-color: var(--fg-gray--200, var(--fg-fallback-gray--200));
      }

      .order-item-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      fg-loader.top {
        display: block;
        margin-top: 52px;
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        fg-order-history-filters {
          display: block;
          margin-bottom: 24px;
        }

        .table-head {
          gap: 24px;
          padding: 0 24px;
          display: grid;
        }

        .order-item {
          gap: 24px;
          padding: 26px 24px;
          grid-template-columns: repeat(5, minmax(100px, 1fr)) 24px;
        }

        .order-item-info strong span {
          display: none;
        }

        .order-item-reference strong,
        .order-item-customer strong {
          display: none;
        }

        .order-item-reference,
        .order-item-customer,
        .order-item-total {
          display: block;
        }

        .order-item-reference span {
          display: block;
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_EXTRA_LARGE_PX - 1}px) {
        .order-item-info {
          flex-direction: column;
          gap: 8px;
        }

        .order-item-info strong,
        .order-item-reference,
        .order-item-customer {
          display: flex;
          flex-direction: row;
          gap: 16px;
        }

        .order-item-status {
          grid-row: 2;
        }

        .order-item-reference {
          grid-row: 3;
        }

        .order-item-customer {
          grid-row: 4;
        }

        .order-item-icon {
          grid-row: 1 / 5;
          margin-left: auto;
        }
      }
    `,
  ];
}
